<template>
  <div class="shipping-pricelist">
    <table class="shipping-pricelist__table">
      <thead>
        <tr v-show="countriesForDropDown.length > 2">
          <td :colspan="numberOfColumns">
            <div class="flex items-center justify-center gap-4">
              {{ $t('Země') }}:
              <MoleculesFormsDropDown :options="countriesForDropDown" :placeholder="$t('Vyberte nebo vyhledejte zemi')" v-model="selectedCountry" label-field="name" value-field="id" class="max-w-[400px] !mx-0" />
            </div>
          </td>
        </tr>
        <tr class="shipping-pricelist__table__header">
          <td v-if="countries.length > 1">{{ $t('Cílová země') }}</td>
          <td>{{ $t('Dopravce') }}</td>
          <td v-if="codColumnEnabled" class="shipping-pricelist__table__price">{{ $t('Bankovní převod / Online kartou') }}</td>
          <td v-if="codColumnEnabled" class="shipping-pricelist__table__price">{{ $t('Při převzetí') }}</td>
          <td v-if="deliveryDaysColumnEnabled">{{ $t('Rychlost dodání') }}</td>
          <td v-if="!codColumnEnabled" class="shipping-pricelist__table__price">{{ $t('Cena') }}</td>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in results">
          <td v-if="countries.length > 1">{{ row.countryName }}</td>
          <td class="font-bold">{{ row.shippingName }}</td>
          <td v-if="codColumnEnabled" class="shipping-pricelist__table__price">{{ typeof row.paymentInAdvancePrice !== 'undefined' ? locale.formatPrice(row.price + row.paymentInAdvancePrice, true) : '–' }}</td>
          <td v-if="codColumnEnabled" class="shipping-pricelist__table__price">{{ typeof row.cashOnDeliveryPrice !== 'undefined' ? locale.formatPrice(row.price + row.cashOnDeliveryPrice, true) : '–' }}</td>
          <td v-if="deliveryDaysColumnEnabled">{{ row.deliveryDays }}</td>
          <td v-if="!codColumnEnabled" class="shipping-pricelist__table__price">{{ typeof row.paymentInAdvancePrice !== 'undefined' ? locale.formatPrice(row.price + row.paymentInAdvancePrice, true) : '–' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script setup>

const appConfig = useAppConfig();
const locale = useLocale();
const shopZone = useShopZone().value;
const i18n = useI18n();

const codColumnEnabled = !shopZone.disabledFeatures.includes('SHIPPING_TABLE_COD_PRICE');
const deliveryDaysColumnEnabled = shopZone.allowedFeatures.includes('SHIPPING_TABLE_DELIVERY_SPEED');

// get preferred country from shop zone - if no countries are specified within shopzone, show all countries
const defaultCountryId = shopZone.countryIds?.[0];

// get all preferred countries from all shop zones to hide in dropdown
const allPreferredCountries = appConfig.shopZones.flatMap(zone => zone.countryIds).filter((value, index, self) => self.indexOf(value) === index);

const countries = useState('shippingPriceListCountries', () => null);

if (!countries.value) {
  countries.value = (await useApiFetch(`/api/shippingsByCountries?${defaultCountryId ? `countryId=${defaultCountryId}` : ''}&languageId=${locale.getLocale().languageId}&currencyId=${locale.getLocale().currencyId}`)).country.filter(country => (defaultCountryId === country.id)|| !allPreferredCountries.includes(country.id)).sort((a, b) => a.name.localeCompare(b.name));
}

const countriesForDropDown = [{ id: null, name: i18n.t('Všechny země') }, ...countries.value.filter(q => q.shippingLevels.length > 0)];

const countryByIp = (await useApiFetch(`/api/countryByIP?languageId=${locale.getLocale().languageId}&currencyId=${locale.getLocale().currencyId}`))?.countryByIp.country?.id;

const selectedCountry = ref(countryByIp ?? (defaultCountryId == null ? null : countries.value[0].id));

const numberOfColumns = 2 + (countries.value.length > 1 ? 1 : 0) + (codColumnEnabled ? 1 : 0) + (deliveryDaysColumnEnabled ? 1 : 0);

const results = computed(() => {

  // if country is selected, check if it really exists, otherwise show all countries
  const selectedCountryObject = countries.value.filter(country => country.id === selectedCountry.value || selectedCountry.value == null || (selectedCountry.value !== null && !countries.value.some(someCountry => someCountry.id === selectedCountry.value)));

  return selectedCountryObject.flatMap(country => {
    return country.shippingLevels.filter(shippingLevel => shippingLevel.idShipping !== appConfig.electronicShippingId).map(shippingLevel => {
      return {
        countryName: country.name,
        shippingName: shippingLevel.shipping.name,
        price: shippingLevel.price,
        paymentInAdvancePrice: shippingLevel.payments.find(payment => appConfig.paymentSystemTypeIdsForAdvancePayment.includes(payment.idSystemType))?.price,
        cashOnDeliveryPrice: shippingLevel.payments.find(payment => !appConfig.paymentSystemTypeIdsForAdvancePayment.includes(payment.idSystemType))?.price,
        deliveryDays: shippingLevel.estimatedDeliveryDays
      };
    })
  });
});

</script>
<style lang="postcss">
.shipping-pricelist {

  @apply my-8 leading-6 tracking-normal;

  &__table {
    @apply w-full;

    tr {
      @apply bg-neutral-100 even:bg-neutral-50;
    }

    td {
      @apply p-3 text-left;
    }

    tbody {
      .shipping-pricelist__table__price {
        @apply !text-right;
      }
    }

    thead {
      .shipping-pricelist__table__price {
        @apply max-w-[80px] !text-center;
      }
    }

    &__header {
      @apply font-bold text-neutral-50 !bg-neutral-500;
    }

  }
}
</style>